import React, { createContext, ReactNode, useState, useEffect } from "react";
import { getApiUrl } from "../utils/api";
import { User, Company } from "@/types";
import { companyAtom } from "@/store/company";
import { useAtom } from "jotai";
import { useQuery } from "@tanstack/react-query";
import { ApiResponse } from "@/lib/api";

// Define the shape of our context
interface AuthContextType {
  token: string | null;
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

// Create the context with a default value
const defaultValue: AuthContextType = {
  token: null,
  user: null,
  login: async () => {},
  logout: () => {},
};

// Create and export the context
export const AuthContext = createContext<AuthContextType>(defaultValue);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(
    localStorage.getItem("token"),
  );
  const [user, setUser] = useState<User | null>(null);
  const [company, setCompany] = useAtom(companyAtom);

  // Fetch companies when user has no company assigned
  const { data: companiesData } = useQuery<ApiResponse<Company>>({
    queryKey: ["companies"],
    queryFn: async () => {
      const response = await fetch(getApiUrl("companies/"), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch companies: ${response.status}`);
      }

      return response.json();
    },
    enabled: !!token && !!user && !user.company && !company,
  });

  // Set first company if user has no company and companies are available
  useEffect(() => {
    if (user && !user.company && !company && companiesData?.results && companiesData.results.length > 0) {
      setCompany(companiesData.results[0]);
    }
  }, [user, company, companiesData, setCompany]);

  const fetchCurrentUser = async (authToken: string) => {
    try {
      const response = await fetch(getApiUrl("users/me/"), {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch user data");
      }

      const userData = await response.json();
      setUser(userData);
    } catch (error) {
      console.error("Error fetching user data:", error);
      // If we can't fetch user data, we should probably log out
      logout();
    }
  };

  // Initialize user data if we have a token
  useEffect(() => {
    if (token) {
      fetchCurrentUser(token);
    }
  }, [token]);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(getApiUrl("auth/login/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error("Login failed");
      }

      const data = await response.json();
      const newToken = data.access_token;
      localStorage.setItem("token", newToken);
      setToken(newToken);
      setCompany(data.user.company);

      // Fetch user data after successful login
      await fetchCurrentUser(newToken);
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  // Create the value object
  const contextValue: AuthContextType = {
    token,
    user,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
